import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLandingConfig } from "@/hooks/useLandingConfig";
import SafeImg from "@/components/landing/SafeImg";

// Hero editoriale slow — no video sfocato: fondo cream + fotografia dello studio a destra.
// Testo grande a sinistra con MOLTO respiro, layout ispirato agli editorial slow-living.
// Testi/CTA sovrascrivibili via /admin/landing-config.
export default function LandingHero() {
  const { T } = useLandingConfig();
  const WHATSAPP_URL = T("cta_links.whatsapp_schedule_url", "https://wa.me/393292561466?text=Ciao%20Epic%20Flow!%20Vorrei%20info%20su%20orari%20e%20piani.");
  const eyebrow = T("texts.hero.eyebrow", "Epic Flow · Pilates Reformer Studio · Puglia");
  const t1 = T("texts.hero.title_line1", "Ritrova");
  const t2 = T("texts.hero.title_line2", "il tuo ritmo");
  const t3 = T("texts.hero.title_line3", "naturale.");
  const italicWord = T("texts.hero.title_italic_word", "ritmo");
  const subtitle = T("texts.hero.subtitle", "Un metodo Pilates lento, consapevole, costruito su di te. In studio a Monopoli e presto a Polignano a Mare.");
  const ctaPrimaryLabel = T("texts.hero.cta_primary_label", "Prenota una lezione");
  const ctaSecondaryLabel = T("texts.hero.cta_secondary_label", "Piani & orari");
  const ctaPrimaryHref = T("cta_links.hero_primary_href", "/registrati");
  const heroPhoto = T("images.hero_photo", "");
  const HERO_FALLBACK = "https://thb.tildacdn.net/tild6466-3233-4934-a362-623737366364/-/resize/1200x/IMG_4202_1.jpg";
  const badgeLabel = T("texts.hero.badge_location_label", "Studio attivo");
  const badgeTitle = T("texts.hero.badge_location_title", "Monopoli");
  const badgeAddress = T("texts.hero.badge_location_address", "Via Luigi Indelli 91A");

  // Sostituisci la parola italic nel line2 se presente
  const renderTitleLine = (line) => {
    if (!italicWord || !line?.toLowerCase().includes(italicWord.toLowerCase())) return line;
    const idx = line.toLowerCase().indexOf(italicWord.toLowerCase());
    return (
      <>
        {line.slice(0, idx)}
        <span className="font-editorial text-mauve">{line.slice(idx, idx + italicWord.length)}</span>
        {line.slice(idx + italicWord.length)}
      </>
    );
  };

  return (
    <section data-testid="landing-hero" className="relative bg-primary text-primary-foreground overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-28 md:pb-40">
        {/* Eyebrow */}
        <div className="reveal-up text-[10px] uppercase tracking-[0.42em] text-primary-foreground/70 flex items-center gap-3 mb-14 md:mb-20">
          <span className="w-10 h-px bg-primary-foreground/50" />
          {eyebrow}
        </div>

        <div className="grid md:grid-cols-12 gap-12 md:gap-16 items-center">
          {/* Colonna testo sinistra */}
          <div className="md:col-span-7 lg:col-span-6">
            <h1 className="reveal-up-delay-1 font-serif-display font-light text-6xl sm:text-7xl md:text-[5.5rem] lg:text-[7rem] leading-[0.95] tracking-tight text-primary-foreground">
              {renderTitleLine(t1)}
              <br />
              {renderTitleLine(t2)}
              <br />
              {renderTitleLine(t3)}
            </h1>
            <p className="reveal-up-delay-2 mt-10 text-lg md:text-xl leading-relaxed text-primary-foreground/85 max-w-md font-light">
              {subtitle}
            </p>

            <div className="reveal-up-delay-3 mt-12 flex flex-wrap items-center gap-3">
              <Link
                to={ctaPrimaryHref}
                data-testid="hero-cta-primary"
                className="group inline-flex items-center gap-3 px-8 py-4 rounded-full bg-primary-foreground text-primary text-xs uppercase tracking-[0.28em] hover:opacity-90 transition-all btn-lift"
              >
                {ctaPrimaryLabel}
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" strokeWidth={1.5}/>
              </Link>
              <a
                href={WHATSAPP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="hero-cta-whatsapp"
                className="group inline-flex items-center gap-3 px-7 py-4 rounded-full border border-primary-foreground/40 text-primary-foreground text-xs uppercase tracking-[0.28em] hover:bg-primary-foreground/10 transition-all"
              >
                <svg viewBox="0 0 24 24" className="w-4 h-4 fill-current" aria-hidden="true">
                  <path d="M17.5 14.4c-.3-.15-1.8-.9-2.07-1-.28-.1-.48-.15-.68.15-.2.3-.78 1-.96 1.2-.18.2-.35.22-.65.08-.3-.15-1.27-.47-2.42-1.5-.9-.8-1.5-1.79-1.67-2.09-.18-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.18.2-.3.3-.5.1-.2.05-.37-.03-.52-.08-.15-.68-1.65-.93-2.25-.25-.6-.5-.51-.68-.52h-.58c-.2 0-.52.08-.8.38-.28.3-1.05 1.02-1.05 2.5s1.08 2.9 1.23 3.1c.15.2 2.1 3.2 5.08 4.5.71.3 1.27.48 1.7.62.71.23 1.36.2 1.87.12.57-.08 1.8-.73 2.05-1.44.25-.7.25-1.3.18-1.44-.08-.13-.28-.2-.58-.35zM12 2C6.48 2 2 6.48 2 12c0 1.9.53 3.67 1.44 5.19L2 22l4.94-1.42A9.96 9.96 0 0 0 12 22c5.52 0 10-4.48 10-10S17.52 2 12 2z"/>
                </svg>
                {ctaSecondaryLabel}
              </a>
            </div>
          </div>

          {/* Colonna foto destra — arched top per parità con reference creami */}
          <div className="reveal-up-delay-2 md:col-span-5 lg:col-span-6 relative">
            <div className="relative aspect-[4/5] arched-top">
              <SafeImg
                src={heroPhoto}
                fallback={HERO_FALLBACK}
                alt="Sala Reformer Epic Flow"
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-4 md:-left-8 bg-plum-dark text-cream rounded-3xl p-5 shadow-xl max-w-[260px]">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] text-cream/75 mb-2">
                <span className="w-2 h-2 rounded-full bg-cream animate-pulse" />
                {badgeLabel}
              </div>
              <div className="font-serif-display italic text-xl leading-tight">{badgeTitle}</div>
              <div className="text-xs text-cream/70 mt-1">{badgeAddress}</div>
            </div>
          </div>
        </div>
      </div>
      {/* Decoro discreto — cerchi radiali morbidi */}
      <div className="pointer-events-none absolute -left-32 -bottom-32 w-96 h-96 rounded-full border border-primary-foreground/15" />
      <div className="pointer-events-none absolute -left-56 -bottom-56 w-[36rem] h-[36rem] rounded-full border border-primary-foreground/10" />
    </section>
  );
}
